/**
 * DeepScanHealthBadge — small owner-only 24h scan-failure health badge
 * for the AdminCost dashboard.
 *
 * Green: 0 failures in the last 24h
 * Amber: 1–2 failures
 * Red:   3+ failures
 *
 * Clicking the pill reveals an in-card details panel with each affected
 * site, failure type, timestamp, and synthetic scan id — no navigation,
 * no expansion into a full monitoring project. Deliberately lightweight:
 * one endpoint call, one collapsible panel, one data-testid per row.
 */
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import api from "../lib/api";

const TONE = {
  green: { pill: "bg-emerald-50 text-emerald-700 border-emerald-100", dot: "bg-emerald-500" },
  amber: { pill: "bg-amber-50 text-amber-700 border-amber-100",       dot: "bg-amber-500" },
  red:   { pill: "bg-red-50 text-red-700 border-red-100",             dot: "bg-red-500" },
};

const TYPE_LABEL = {
  timeout: "Timeout",
  crash: "Crash",
  blocked: "Blocked access",
};

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "—";
    const min = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    if (min < 24 * 60) return `${Math.round(min / 60)}h ago`;
    return `${Math.round(min / 1440)}d ago`;
  } catch { return "—"; }
}

export default function DeepScanHealthBadge() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get("/admin/deep-scan-health");
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setErr(String(e?.message || e));
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (err || !data) return null;
  const tone = TONE[data.status] || TONE.green;
  const summary = data.count === 0
    ? "No failures"
    : `${data.count} in 24h`;

  return (
    <section
      className="mt-4 rounded-2xl bg-white border border-black/5 shadow-soft overflow-hidden"
      data-testid="deep-scan-health-card"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-charcoal-50/60 transition-colors text-left"
        data-testid="deep-scan-health-toggle"
      >
        <span className={`w-2 h-2 rounded-full ${tone.dot} ring-4 ring-black/5`} />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-500">
            Deep Scan Health · Last 24h
          </p>
          <p className="text-sm text-charcoal-800 font-medium mt-0.5">
            {summary}
            {data.last_failed_at && (
              <span className="text-charcoal-400 font-normal ml-2">
                · latest {timeAgo(data.last_failed_at)}
              </span>
            )}
          </p>
        </div>
        <span
          className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border ${tone.pill}`}
          data-testid="deep-scan-health-status-pill"
        >
          {data.status}
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-charcoal-400" /> : <ChevronRight className="w-4 h-4 text-charcoal-400" />}
      </button>

      {open && (
        <div className="border-t border-black/5 px-5 py-4 bg-charcoal-50/40" data-testid="deep-scan-health-panel">
          {data.count > 0 && (
            <div className="flex gap-4 mb-4 text-[11px] font-mono uppercase tracking-wider text-charcoal-500">
              <span data-testid="dsh-count-timeout">Timeouts · {data.by_type?.timeout ?? 0}</span>
              <span data-testid="dsh-count-crash">Crashes · {data.by_type?.crash ?? 0}</span>
              <span data-testid="dsh-count-blocked">Blocked · {data.by_type?.blocked ?? 0}</span>
            </div>
          )}
          {data.items?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10.5px] font-mono uppercase tracking-wider text-charcoal-400">
                    <th className="text-left py-2 pr-3">Site</th>
                    <th className="text-left py-2 pr-3">Type</th>
                    <th className="text-left py-2 pr-3">When</th>
                    <th className="text-left py-2 pr-3">Scan ID</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr
                      key={it.scan_id}
                      className="border-t border-black/5 text-charcoal-700"
                      data-testid={`dsh-row-${it.scan_id}`}
                    >
                      <td className="py-2 pr-3 font-medium">
                        {it.org_name || <span className="text-charcoal-400">—</span>}
                        {it.site && (
                          <span className="block text-[11px] font-mono text-charcoal-400 truncate max-w-[240px]">
                            {it.site}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <span className="inline-flex items-center gap-1.5">
                          <AlertTriangle className="w-3 h-3 text-charcoal-400" />
                          {TYPE_LABEL[it.failure_type] || it.failure_type}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-[12px] text-charcoal-500">{timeAgo(it.failed_at)}</td>
                      <td className="py-2 pr-3 text-[11px] font-mono text-charcoal-500">{it.scan_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-charcoal-500">No failures in the last 24 hours.</p>
          )}
        </div>
      )}
    </section>
  );
}
